import { NamedView, PersistableView } from "@common/types/views/views";
import {
    GeneralLedgerColumn,
    GeneralLedgerView,
} from "@common/types/views/generalLedgerView";
import { AccountingReportFilters } from "../types";
import {
    buildAccountingReportBuiltInViewFilters,
    buildAccountingReportViewFilters,
    buildAccountingReportViewPeriodComparisonFilters,
} from "./accountingReportViewMapper";
import {
    getAccountingReportBuiltInViewIds,
    getAccountingReportBuiltInViewSettings,
    isAccountingReportBuiltInView,
} from "./accountingReportBuiltInViews";
import {
    AccountingReportViewId,
    getAccountingReportViewDefinition,
} from "./accountingReportViewRegistry";

export function buildAccountingReportBuiltInViews({
    reportId,
    filters,
    defaultGlView,
}: {
    reportId: AccountingReportViewId;
    filters: Pick<AccountingReportFilters, "consolidationGroupId">;
    defaultGlView?: GeneralLedgerView;
}): Array<NamedView<PersistableView>> {
    const { type, defaultCycle, hasTaxView } =
        getAccountingReportViewDefinition(reportId, filters);
    return getAccountingReportBuiltInViewIds({ hasTaxView }).map((viewId) => {
        const settings = getAccountingReportBuiltInViewSettings({
            viewId,
            defaultCycle,
        });
        return {
            id: viewId,
            name: settings.name,
            type,
            columns: defaultGlView?.columns ?? [],
            filters: buildAccountingReportBuiltInViewFilters({
                reportId,
                filters,
                settings,
            }),
            options: defaultGlView ? defaultGlView.options : null,
        };
    });
}

export function buildAccountingReportPersistView({
    reportId,
    filters,
    glView,
}: {
    reportId: AccountingReportViewId;
    filters: AccountingReportFilters;
    glView?: GeneralLedgerView;
}): PersistableView {
    return {
        type: getAccountingReportViewDefinition(reportId, filters).type,
        columns: glView?.columns ?? [],
        filters: buildAccountingReportViewFilters({ reportId, filters }),
        options: glView ? glView.options : null,
    };
}

export function accountingReportViewForComparison({
    reportId,
    filters,
    persistView,
    baseViewId,
}: {
    reportId: AccountingReportViewId;
    filters: AccountingReportFilters;
    persistView: PersistableView;
    baseViewId: string | null;
}): PersistableView {
    if (!isAccountingReportBuiltInView(baseViewId)) {
        return persistView;
    }

    return {
        ...persistView,
        filters: buildAccountingReportViewPeriodComparisonFilters({
            reportId,
            filters,
        }),
    };
}

export function nextGeneralLedgerLayout({
    selected,
    defaultView,
}: {
    selected: PersistableView & { id?: string };
    defaultView: GeneralLedgerView;
}): GeneralLedgerView {
    if (isAccountingReportBuiltInView(selected.id)) {
        return defaultView;
    }

    const columns: GeneralLedgerView["columns"] = [];
    for (const column of selected.columns) {
        if (isGeneralLedgerColumnId(column.id)) {
            columns.push(
                column.options
                    ? { id: column.id, options: column.options }
                    : { id: column.id },
            );
        }
    }

    return {
        ...defaultView,
        columns: columns.length > 0 ? columns : defaultView.columns,
        options: selected.options ?? defaultView.options,
    };
}

function isGeneralLedgerColumnId(id: string): id is GeneralLedgerColumn {
    return Object.values(GeneralLedgerColumn).some(
        (columnId) => columnId === id,
    );
}
